import React from 'react';
import { motion } from 'framer-motion';
import { Shirt, CreditCard, MapPin, MessageCircle, Star, Ruler } from 'lucide-react';

const HowItWorksCustomersSection: React.FC = () => {
  const features = [
    {
      icon: Shirt,
      title: 'Place Your Order',
      description: 'Easily submit your custom tailoring requests through our intuitive platform.',
    },
    {
      icon: CreditCard,
      title: 'Secure Payment',
      description: 'Pay for your order securely with various convenient payment options.',
    },
    {
      icon: MapPin,
      title: 'Track Progress',
      description: 'Stay updated on your order status from cutting to final delivery.',
    },
    {
      icon: MessageCircle,
      title: 'Chat with Your Tailor',
      description: 'Communicate directly with your tailor for fittings or adjustments.',
    },
    {
      icon: Star,
      title: 'Review Tailors',
      description: 'Share your experience and rate your tailor craftsmanship and service.',
    },
    {
      icon: Ruler,
      title: 'Measurement Management',
      description: 'Manage and store your precise measurements for future orders easily.',
    },
  ];

  return (
    <section className="bg-gray-light py-16 lg:py-20">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingLeft: 'clamp(16px, 8.89vw, 128px)', paddingRight: 'clamp(16px, 8.89vw, 128px)' }}>
        {/* Section Header */}
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-opensans text-[36px] leading-[40px] text-text-primary font-bold mb-4"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            Customers: Your Seamless Tailoring Journey
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-manrope text-[18px] leading-[28px] text-text-secondary max-w-[768px] mx-auto"
          >
            From personalized orders to perfect fits, we guide you every step of the way with clarity and convenience.
          </motion.p>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center lg:justify-items-start" style={{ maxWidth: '1194px', margin: '0 auto' }}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-card hover:shadow-card-hover transition-all duration-300 w-full"
                style={{ maxWidth: '374px', minHeight: '194px' }}
              >
                {/* Icon */}
                <div className="w-10 h-10 mb-4 flex items-center justify-center">
                  <Icon className="w-10 h-10 text-brand-yellow" />
                </div>

                {/* Title */}
                <h3 
                  className="font-opensans text-[20px] leading-[28px] text-text-primary font-semibold mb-3"
                  style={{ 
                    fontVariationSettings: "'wdth' 100",
                    letterSpacing: '-0.5px'
                  }}
                >
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="font-manrope text-[14px] leading-[20px] text-text-secondary">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksCustomersSection;
